import type { JoinDraft } from "@/app/[locale]/join/schema";

/**
 * Crash/reload resilience for the join wizard. Two independent stores:
 *
 * - Form fields + current step in localStorage (`pf_join_draft`), versioned
 *   with a 24h TTL. The password is NEVER persisted — a restored session asks
 *   the user to re-enter it.
 * - Picked photo files as blobs in IndexedDB (`pf-join`/`photos`), so a failed
 *   post-register upload can be retried from the waiting room without
 *   re-picking.
 *
 * Every function is a safe no-op when the underlying storage is missing or
 * broken (private browsing, quota, no IndexedDB): reads resolve empty, writes
 * resolve without effect. The wizard then simply degrades to memory-only.
 */

const DRAFT_KEY = "pf_join_draft";
const DRAFT_VERSION = 1;
const DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

/** Join form fields persisted across reloads — everything except the password. */
export type JoinDraftFields = Omit<JoinDraft, "password">;

export interface JoinDraftRecord {
  step: number;
  fields: JoinDraftFields;
}

/** Persisted envelope; `fields` is trusted only when `v` matches. */
interface StoredDraft {
  v: number;
  savedAt: number;
  step: number;
  fields: JoinDraftFields;
}

/**
 * Saves the draft fields and step. Accepts a full `JoinDraft` for convenience
 * but strips the password before writing, unconditionally.
 */
export function saveJoinDraft(
  fields: JoinDraftFields & { password?: string },
  step: number,
): void {
  const safe = { ...fields };
  delete safe.password;
  const record: StoredDraft = {
    v: DRAFT_VERSION,
    savedAt: Date.now(),
    step,
    fields: safe,
  };
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(record));
  } catch {
    /* storage unavailable — draft persistence is best-effort */
  }
}

/** Loads the saved draft; stale (>24h) or wrong-version records are dropped. */
export function loadJoinDraft(): JoinDraftRecord | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw) as Partial<StoredDraft>;
    if (
      record.v !== DRAFT_VERSION ||
      typeof record.savedAt !== "number" ||
      typeof record.fields !== "object" ||
      record.fields === null ||
      Date.now() - record.savedAt > DRAFT_TTL_MS
    ) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return {
      step: typeof record.step === "number" ? record.step : 1,
      fields: record.fields,
    };
  } catch {
    return null;
  }
}

export function clearJoinDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* ignore */
  }
}

// --- Photo blobs (IndexedDB) ------------------------------------------------

const DB_NAME = "pf-join";
const DB_VERSION = 1;
const PHOTO_STORE = "photos";

interface StoredPhoto {
  id: string;
  name: string;
  type: string;
  blob: Blob;
  addedAt: number;
}

/** A photo restored from the draft store, rebuilt as a regular `File`. */
export interface DraftPhoto {
  id: string;
  file: File;
}

/** Opens the draft database, or resolves null when IndexedDB is unusable. */
function openPhotoDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(PHOTO_STORE)) {
          request.result.createObjectStore(PHOTO_STORE, { keyPath: "id" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/**
 * Runs one transaction against the photo store and resolves `fallback` on any
 * failure, so callers never have to handle storage errors.
 */
function withPhotoStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore, done: (value: T) => void) => void,
  fallback: T,
): Promise<T> {
  return new Promise((resolve) => {
    void openPhotoDb().then((db) => {
      if (!db) {
        resolve(fallback);
        return;
      }
      let result = fallback;
      const finish = (value: T) => {
        result = value;
      };
      try {
        const tx = db.transaction(PHOTO_STORE, mode);
        tx.oncomplete = () => {
          db.close();
          resolve(result);
        };
        tx.onerror = () => {
          db.close();
          resolve(fallback);
        };
        tx.onabort = () => {
          db.close();
          resolve(fallback);
        };
        run(tx.objectStore(PHOTO_STORE), finish);
      } catch {
        db.close();
        resolve(fallback);
      }
    });
  });
}

/** Stores a picked file; resolves its draft id, or null when unavailable. */
export function addDraftPhoto(file: File, stableId?: string): Promise<string | null> {
  const id = stableId ??
    (typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const record: StoredPhoto = {
    id,
    name: file.name,
    type: file.type,
    blob: file,
    addedAt: Date.now(),
  };
  return withPhotoStore<string | null>(
    "readwrite",
    (store, done) => {
      store.put(record);
      done(id);
    },
    null,
  );
}

export function removeDraftPhoto(id: string): Promise<void> {
  return withPhotoStore<void>(
    "readwrite",
    (store) => {
      store.delete(id);
    },
    undefined,
  );
}

/** Lists stored photos oldest-first, rebuilt as `File`s ready to upload. */
export function listDraftPhotos(): Promise<DraftPhoto[]> {
  return withPhotoStore<DraftPhoto[]>(
    "readonly",
    (store, done) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const rows = (request.result as StoredPhoto[])
          .slice()
          .sort((a, b) => a.addedAt - b.addedAt);
        done(
          rows.map((row) => ({
            id: row.id,
            file: new File([row.blob], row.name, { type: row.type }),
          })),
        );
      };
    },
    [],
  );
}

export function clearDraftPhotos(): Promise<void> {
  return withPhotoStore<void>(
    "readwrite",
    (store) => {
      store.clear();
    },
    undefined,
  );
}
